// onboarding-step.js
import get from "lodash/get";
import welcome from "onboarding/welcome/welcomeOnboardingTrack";
import apm from "onboarding/apm/apmOnboardingTrack";
import host from "onboarding/host/hostOnboardingTrack";
import aws from "onboarding/aws/awsOnboardingTrack";
import { flagOn, ROLLOUT_REENABLE_ONBOARDING } from "components/Flag";

const SECTIONS = { apm, aws, host, welcome };
const KNOWN_STEPS = {
  ...apm.byFullName,
  ...aws.byFullName,
  ...host.byFullName,
  ...welcome.byFullName,
};

export const isValidSectionName = sectionName => !!SECTIONS[sectionName];
export const getOnboardingStep = ({ sectionName, stepKey, fullName }) => {
  if (fullName) return KNOWN_STEPS[fullName] || {};
  const section = SECTIONS[sectionName];
  if (!section || !stepKey) return {};
  return get(SECTIONS[sectionName], `byKey.${stepKey}`, {});
};

export const INITIAL_STATE = {
  completed: false,
  initial: false,
  reload: false,
  ready: false,
  sectionName: null,
  standalone: false,
  stepKey: null,
  fullName: null,
};

// helpers
export const PERSIST_TO_COLUMN = "step";

const standalonePrefix = "Complete Additional ";
const standaloneRegex = new RegExp(`^${standalonePrefix}`);
const isCompleted = step => /Complete/.test(step || "");
export const parseStepName = step => {
  const fullName = (step || "").replace(standaloneRegex, "");
  const knownStep = getOnboardingStep({ fullName });
  if (!knownStep) return {};
  const { sectionName, stepKey, stepPath } = knownStep;
  const completed = isCompleted(step);
  const standalone = completed && stepKey !== "complete";
  return { completed, sectionName, standalone, stepKey, fullName, stepPath };
};

export const serializeCurrentStep = (state = {}, compareTo = {}) => {
  // serialize to string for saving to onboarding state
  const { fullName, stepKey } = parseStepName(state.fullName);
  if (!stepKey || stepKey === "invite") return {};
  const persistedStep = compareTo[PERSIST_TO_COLUMN] || "";
  const completed = state.completed || isCompleted(persistedStep);
  const standalone = state.standalone || (completed && stepKey !== "complete");
  const prefix = standalone ? standalonePrefix : "";
  const prefixedStepName = `${prefix}${fullName}`;
  if (prefixedStepName === persistedStep) return {};
  return {
    [PERSIST_TO_COLUMN]: prefixedStepName,
  };
};

export const deserializeOnboardingStepState = (onboardingState = {}) => {
  // deserialize from string read from onboarding state
  const persistedStep = onboardingState[PERSIST_TO_COLUMN] || "";
  return {
    ...INITIAL_STATE,
    ...parseStepName(persistedStep),
  };
};

// action types
const SET_STEP = "appoptics/onboarding/SET_STEP";
const SET_ONBOARDING_STEP_STATE =
  "appoptics/onboarding/SET_ONBOARDING_STEP_STATE";
const INVALIDATE = "appoptics/onboarding/INVALIDATE";

// action creators
export const setOnboardingStepState = onboardingState => {
  return {
    type: SET_ONBOARDING_STEP_STATE,
    onboardingState,
  };
};

export const setCurrentStep = fullName => {
  return {
    type: SET_STEP,
    ...parseStepName(fullName),
  };
};

export const invalidateOnboardingStep = () => ({
  type: INVALIDATE,
});

// reducer
const onboardingStepReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case SET_STEP: {
      const completed = flagOn(ROLLOUT_REENABLE_ONBOARDING)
        ? true
        : state.completed || state.stepKey === "complete";
      return {
        ...state,
        ready: true,
        initial: !state.ready,
        sectionName: action.sectionName,
        stepKey: action.stepKey,
        fullName: action.fullName,
        completed: completed || action.stepKey === "complete",
        standalone:
          state.standalone || (completed && action.stepKey !== "complete"),
      };
    }
    case SET_ONBOARDING_STEP_STATE:
      return {
        ...state,
        ...deserializeOnboardingStepState(action.onboardingState),
        initial: true,
        ready: true,
        reload: false,
      };
    case INVALIDATE:
      return {
        ...state,
        reload: true,
      };
    default:
      return state;
  }
};
export default onboardingStepReducer;
